import { LOGOUT } from '@comeon/actions-public';
import { insertTrackEvent } from '@comeon/analytics';
import reducerRegistry from '@comeon/reducer-registry';
import { showNotification } from '../../../notifications/ducks/notifications';
import type { CasinoSharedAction, CasinoSharedThunkAction } from '../../ducks/types';
import {
	EXCLUSIVE_GAME_WIDGET_SUBSCRIBE,
	EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN,
	EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE,
	EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS
} from './action-names';
import { fetchSubscriptions, subscribe, unsubscribe, Widget } from './api';
import type { State } from './types';

const REDUCER_NAME = 'exclusiveGameWidgets';

export const subscribeGameWidget =
	(widget: Widget, isLiveCasinoLocation: boolean): CasinoSharedThunkAction =>
	async dispatch => {
		dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget });

		const response = await subscribe(widget);

		if (response?.status === 'FAILURE') {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget });
			return;
		}

		if (!response) {
			dispatch(
				showNotification({
					messageKey: 'WIDGET_SUBSCRIPTION_FAILED',
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget });
			return;
		}

		insertTrackEvent({
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino',
			action: 'WidgetExclusive_Click_RemindMe',
			label: 'Reminder set',
			value: ''
		});
	};

export const unsubscribeGameWidget =
	(widget: Widget): CasinoSharedThunkAction =>
	async dispatch => {
		dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget });

		const response = await unsubscribe(widget);

		if (response?.status === 'FAILURE') {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget });

			return;
		}

		if (!response) {
			dispatch(
				showNotification({
					messageKey: 'WIDGET_UNSUBSCRIBE_FAILED',
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget });
		}
	};

export const updateSubscriptions = (payload: Widget[] | null): CasinoSharedAction => ({
	type: EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS,
	payload
});

export const getSubscriptions =
	(playerId: string): CasinoSharedThunkAction =>
	async (dispatch, getState) => {
		const { subscriptions } = getState().exclusiveGameWidgets;
		if (subscriptions) return;

		const response = await fetchSubscriptions(playerId);

		if (response?.status === 'FAILURE' || !response) {
			return;
		}

		dispatch(updateSubscriptions(response.result || []));
	};

export const toggleModalOpen = (payload: boolean): CasinoSharedAction => ({
	type: EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN,
	payload
});

const initialState: State = {
	subscriptions: null,
	modalOpen: false
};

const reducer = (state = initialState, action: CasinoSharedAction) => {
	switch (action.type) {
		case EXCLUSIVE_GAME_WIDGET_SUBSCRIBE:
			return {
				...state,
				subscriptions: [...(state.subscriptions ?? []), action.payload]
			};
		case EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE:
			return {
				...state,
				subscriptions: (state.subscriptions ?? []).filter(
					subscription => subscription.widgetId !== action.payload.widgetId
				)
			};
		case EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS:
			return {
				...state,
				subscriptions: action.payload
			};

		case EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN:
			return {
				...state,
				modalOpen: action.payload
			};
		case LOGOUT:
			return initialState;
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);
export default reducer;
